// LightweightDiagramsPanel.js — decision scaffolding for Feature Plans (Propagation + Ownership).

import React, { useState } from "react";
import {
  sortByConfidence,
  displayChipLabel,
  shortenReason,
  LANE_DEFAULT_VISIBLE,
  RELATIONSHIP_DEFAULT_VISIBLE,
} from "./lightweightDiagramDisplay.js";
import "./LightweightDiagramsPanel.css";

const NODE_KIND_LABEL = {
  request: "request",
  entrypoint: "entry",
  route: "route",
  component: "UI",
  service: "service",
  model: "data",
  worker: "worker",
  queue: "queue",
  retrieval: "retrieval",
  planner: "planner",
  test: "test",
  config: "config",
  file: "file",
  unknown: "?",
};

const RELATION_LABEL = {
  calls: "calls",
  imports: "imports",
  routes_to: "routes to",
  reads: "reads",
  writes: "writes",
  queues: "queues",
  renders: "renders",
  validates: "validates",
  configures: "configures",
  likely_affects: "likely affects",
  unknown: "related to",
};

const IMPORTANCE_LABEL = {
  must_edit: "must edit",
  likely_edit: "likely edit",
  optional: "optional",
  validation: "validation",
  unknown: "unknown",
};

const RISK_LABEL = {
  contract_change: "Contract change",
  async_boundary: "Async boundary",
  data_shape: "Data shape",
  auth_or_permissions: "Auth / permissions",
  state_persistence: "State persistence",
  ui_expectation: "UI expectation",
  test_gap: "Test gap",
  unknown: "Unknown risk",
};

function ConfidenceBadge({ confidence }) {
  const c = (confidence || "unknown").toLowerCase();
  return (
    <span className={`ld-badge ld-conf ld-conf-${c}`} title={confidenceTitle(c)}>
      {c}
    </span>
  );
}

function confidenceTitle(c) {
  if (c === "strong") return "Strong: grounded in plan / evidence / import / reference.";
  if (c === "inferred") return "Inferred: conservative path or role inference.";
  if (c === "weak") return "Weak: plausible but unproven — verify before relying on it.";
  return "Confidence unknown.";
}

function OverallConfidence({ confidence }) {
  const c = (confidence || "low").toLowerCase();
  return <span className={`ld-badge ld-overall ld-overall-${c}`}>{c} confidence</span>;
}

function FileChip({ label, filePath, kind, confidence, reason }) {
  const display = displayChipLabel(label, filePath);
  const title = [filePath || label, reason].filter(Boolean).join("\n");
  return (
    <span className={`ld-chip ld-chip-${kind || "file"}`} title={title}>
      {kind && NODE_KIND_LABEL[kind] && kind !== "file" && (
        <span className="ld-chip-kind">{NODE_KIND_LABEL[kind]}</span>
      )}
      <code className="ld-chip-label">{display}</code>
      {confidence && <ConfidenceBadge confidence={confidence} />}
    </span>
  );
}

function DiagramLegend({ compact = false }) {
  return (
    <div
      className={`ld-legend ${compact ? "ld-legend-compact" : ""}`}
      aria-label="Diagram legend"
    >
      <span className="ld-legend-title">Legend</span>
      <span className="ld-legend-item">
        <span className="ld-badge ld-conf ld-conf-strong">strong</span> evidence-backed
      </span>
      <span className="ld-legend-item">
        <span className="ld-badge ld-conf ld-conf-inferred">inferred</span> conservative
      </span>
      <span className="ld-legend-item">
        <span className="ld-badge ld-conf ld-conf-weak">weak</span> verify first
      </span>
    </div>
  );
}

function DiagramSectionShell({ children }) {
  return (
    <div className="plan-content-block lightweight-diagrams-block">
      <h5>Lightweight diagrams</h5>
      <div className="ld-intro">
        Decision scaffolding from grounded plan evidence — not architecture art.
      </div>
      {children}
    </div>
  );
}

function DiagramIncompleteState() {
  return (
    <DiagramSectionShell>
      <div className="ld-empty ld-safe-state">
        <div className="ld-empty-title">Diagram signal unavailable</div>
        <div className="ld-empty-sub">
          Diagram signal unavailable until the Feature Plan is completed.
        </div>
      </div>
    </DiagramSectionShell>
  );
}

function DiagramNoSafeState({ diagnostics }) {
  const reasons = (diagnostics?.omittedReasons || []).filter(Boolean);
  return (
    <DiagramSectionShell>
      <div className="ld-empty">
        <div className="ld-empty-title">No safe diagram generated from available evidence.</div>
        <div className="ld-empty-sub">
          RenderRepo intentionally did not infer unsupported propagation or ownership.
        </div>
        {reasons.length > 0 && (
          <ul className="ld-empty-reasons">
            {reasons.slice(0, 3).map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
        )}
      </div>
    </DiagramSectionShell>
  );
}

function DiagramSuppressedState({ validation, diagnostics }) {
  const issueCodes = (validation?.errors || [])
    .filter((e) => e.severity === "error")
    .map((e) => e.code)
    .slice(0, 5);
  const hint = diagnostics?.omittedReasons?.find((r) =>
    /trust boundary|validation|insufficient/i.test(r)
  );
  return (
    <DiagramSectionShell>
      <div className="ld-empty ld-safe-state">
        <div className="ld-empty-title">Diagram suppressed by validation.</div>
        <div className="ld-empty-sub">
          RenderRepo withheld diagram details because the bundle was not safe to render.
          {hint ? ` ${hint}` : ""}
        </div>
        {issueCodes.length > 0 && (
          <div className="ld-issue-codes">Issue codes: {issueCodes.join(", ")}.</div>
        )}
      </div>
    </DiagramSectionShell>
  );
}

function UnknownsBox({ unknowns }) {
  if (!unknowns || unknowns.length === 0) return null;
  return (
    <div className="ld-unknowns">
      <div className="ld-unknowns-title">Unknowns &amp; things to verify</div>
      <ul>
        {unknowns.map((u) => (
          <li key={u.id} className="ld-unknown-item">
            <span className="ld-unknown-label">{u.label}</span>
            <span className="ld-unknown-reason">{u.reason}</span>
            {u.suggestedCheck && (
              <span className="ld-unknown-check">Check: {u.suggestedCheck}</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function StageLane({ stage, nodeById, defaultVisible = LANE_DEFAULT_VISIBLE }) {
  const [expanded, setExpanded] = useState(false);
  const nodes = sortByConfidence(
    stage.nodeIds.map((id) => nodeById.get(id)).filter(Boolean)
  );
  const visible = expanded ? nodes : nodes.slice(0, defaultVisible);
  const overflow = nodes.length - defaultVisible;

  return (
    <div className="ld-lane" role="listitem">
      <div className="ld-lane-header">{stage.label}</div>
      <div className="ld-lane-body">
        {visible.map((n) => (
          <FileChip
            key={n.id}
            label={n.label}
            filePath={n.filePath}
            kind={n.kind}
            confidence={n.confidence}
            reason={n.reason}
          />
        ))}
        {overflow > 0 && !expanded && (
          <button
            type="button"
            className="ld-lane-overflow"
            onClick={() => setExpanded(true)}
            aria-expanded={false}
          >
            +{overflow} more
          </button>
        )}
        {expanded && overflow > 0 && (
          <button
            type="button"
            className="ld-lane-overflow"
            onClick={() => setExpanded(false)}
            aria-expanded={true}
          >
            Show less
          </button>
        )}
      </div>
    </div>
  );
}

function RelationshipRow({ edge, nodeById }) {
  const from = nodeById.get(edge.from);
  const to = nodeById.get(edge.to);
  const fromLabel = from ? displayChipLabel(from.label, from.filePath) : edge.from;
  const toLabel = to ? displayChipLabel(to.label, to.filePath) : edge.to;
  const shortReason = shortenReason(edge.reason);

  return (
    <li
      className={`ld-edge ld-edge-${edge.confidence || "unknown"}`}
      title={edge.reason || undefined}
    >
      <div className="ld-edge-primary">
        <code className="ld-edge-node">{fromLabel}</code>
        <span className="ld-edge-rel">
          {RELATION_LABEL[edge.relation] || edge.relation}
          <span className="ld-edge-arrow" aria-hidden="true">→</span>
        </span>
        <code className="ld-edge-node">{toLabel}</code>
        <ConfidenceBadge confidence={edge.confidence} />
      </div>
      {shortReason && <span className="ld-edge-reason">{shortReason}</span>}
    </li>
  );
}

function KeyRelationships({ edges, nodeById }) {
  const [showAll, setShowAll] = useState(false);
  const sorted = sortByConfidence(edges);
  const overflow = sorted.length - RELATIONSHIP_DEFAULT_VISIBLE;
  const visible = showAll ? sorted : sorted.slice(0, RELATIONSHIP_DEFAULT_VISIBLE);

  if (sorted.length === 0) return null;

  return (
    <div className="ld-edges">
      <div className="ld-edges-header">
        <div className="ld-edges-title">Key relationships</div>
        {!showAll && overflow > 0 && (
          <span className="ld-edges-count">{sorted.length} total</span>
        )}
      </div>
      <ul>
        {visible.map((e) => (
          <RelationshipRow key={e.id} edge={e} nodeById={nodeById} />
        ))}
      </ul>
      {overflow > 0 && (
        <button
          type="button"
          className="ld-expand-toggle"
          onClick={() => setShowAll((v) => !v)}
          aria-expanded={showAll}
        >
          {showAll ? "Show fewer relationships" : `Show all relationships (+${overflow})`}
        </button>
      )}
    </div>
  );
}

export function PropagationMap({ propagation, diagnostics }) {
  if (!propagation) return null;
  const nodeById = new Map((propagation.nodes || []).map((n) => [n.id, n]));

  return (
    <div className="ld-propagation">
      <div className="ld-summary ld-summary-compact">
        <OverallConfidence confidence={propagation.confidence} />
        {propagation.summary && (
          <p className="ld-summary-text" title={propagation.summary}>
            {propagation.summary}
          </p>
        )}
      </div>

      <div className="ld-flow-heading">Change flow</div>
      <div className="ld-lanes" role="list">
        {(propagation.stages || []).map((stage) => (
          <StageLane key={stage.id} stage={stage} nodeById={nodeById} />
        ))}
      </div>

      <KeyRelationships edges={propagation.edges || []} nodeById={nodeById} />

      <UnknownsBox unknowns={propagation.unknowns} />

      <DiagramLegend compact />
    </div>
  );
}

function OwnershipMap({ ownership }) {
  if (!ownership) return null;
  const groupById = new Map((ownership.groups || []).map((g) => [g.id, g]));

  return (
    <div className="ld-ownership">
      <div className="ld-summary">
        <OverallConfidence confidence={ownership.confidence} />
        <span className="ld-summary-text">{ownership.summary}</span>
      </div>

      <div className="ld-groups">
        {(ownership.groups || []).map((g) => (
          <div key={g.id} className={`ld-group ld-group-role-${g.role}`}>
            <div className="ld-group-header">
              <span className="ld-group-label">{g.label}</span>
              <span className={`ld-badge ld-role ld-role-${g.role}`}>{g.role}</span>
              <ConfidenceBadge confidence={g.confidence} />
            </div>
            <div className="ld-group-reason">{g.reason}</div>
            <div className="ld-group-files">
              {g.files.map((f) => (
                <span
                  key={f.path}
                  className={`ld-chip ld-imp-${f.importance}`}
                  title={[f.path, f.reason].filter(Boolean).join("\n")}
                >
                  <span className="ld-chip-kind">{IMPORTANCE_LABEL[f.importance] || f.importance}</span>
                  <code className="ld-chip-label">{f.path.split("/").pop()}</code>
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>

      {ownership.boundaries && ownership.boundaries.length > 0 && (
        <div className="ld-boundaries">
          <div className="ld-boundaries-title">Coordination boundaries</div>
          <ul>
            {ownership.boundaries.map((b) => {
              const from = groupById.get(b.fromGroupId);
              const to = groupById.get(b.toGroupId);
              return (
                <li key={b.id} className="ld-boundary" title={b.reason}>
                  <span className="ld-boundary-pair">
                    {from ? from.label : b.fromGroupId}
                    <span className="ld-edge-arrow" aria-hidden="true">↔</span>
                    {to ? to.label : b.toGroupId}
                  </span>
                  <span className={`ld-badge ld-risk ld-risk-${b.coordinationRisk}`}>
                    {RISK_LABEL[b.coordinationRisk] || b.coordinationRisk}
                  </span>
                  <span className="ld-boundary-reason">{b.reason}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <UnknownsBox unknowns={ownership.unknowns} />
    </div>
  );
}

/**
 * @param {{ bundle?: object, planStatus?: string }} props
 */
export default function LightweightDiagramsPanel({ bundle, planStatus }) {
  const [tab, setTab] = useState("propagation");

  if (planStatus !== "completed") {
    return <DiagramIncompleteState />;
  }

  if (!bundle) {
    return <DiagramNoSafeState diagnostics={null} />;
  }

  const validation = bundle.validation;
  const renderable = validation?.renderable === true;
  const hasPropagation = Boolean(bundle.propagation);
  const hasOwnership = Boolean(bundle.ownership);
  const hasDiagramContent = hasPropagation || hasOwnership;
  const omittedForEvidence =
    validation?.stats?.omittedForInsufficientEvidence ||
    validation?.stats?.hasEmptyState;

  if (!renderable && !hasDiagramContent) {
    if (validation?.errors?.some((e) => e.severity === "error")) {
      return <DiagramSuppressedState validation={validation} diagnostics={bundle.diagnostics} />;
    }
    return <DiagramNoSafeState diagnostics={bundle.diagnostics} />;
  }

  if (!hasDiagramContent || omittedForEvidence) {
    return <DiagramNoSafeState diagnostics={bundle.diagnostics} />;
  }

  return (
    <DiagramSectionShell>
      {(validation?.warnings?.length > 0 ||
        bundle.propagation?.confidence === "low" ||
        bundle.ownership?.confidence === "low") && (
        <div className="ld-uncertainty-banner" role="status">
          Evidence confidence is limited — verify nodes and relationships before relying on this map.
        </div>
      )}

      <div className="ld-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === "propagation"}
          className={`ld-tab ${tab === "propagation" ? "active" : ""}`}
          onClick={() => setTab("propagation")}
        >
          Propagation
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "ownership"}
          className={`ld-tab ${tab === "ownership" ? "active" : ""}`}
          onClick={() => setTab("ownership")}
        >
          Ownership
        </button>
      </div>

      <div className="ld-tab-panel" role="tabpanel">
        {tab === "propagation" ? (
          <PropagationMap propagation={bundle.propagation} diagnostics={bundle.diagnostics} />
        ) : (
          <>
            <DiagramLegend />
            <OwnershipMap ownership={bundle.ownership} />
          </>
        )}
      </div>
    </DiagramSectionShell>
  );
}
